import { useReactiveVar } from '@apollo/client';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';

import FilterDropdown from '../FilterDropdown';
import FilterPill from '../FilterPill';
import { buildMultiChipLabel } from '../helpers';

import { useRegionQuery } from '@/graphql';
import { currentAgencyVar } from '@/util/apollo/cache';

type Props = {
  value: string[] | undefined;
  onApply: (next: string[] | undefined) => void;
  onClear: () => void;
};

const RegionPill = ({ value, onApply, onClear }: Props) => {
  const currentAgency = useReactiveVar(currentAgencyVar);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [staged, setStaged] = useState<string[]>(() => value || []);

  const { data } = useRegionQuery({
    variables: { agencyId: currentAgency!.id },
  });

  const regions = useMemo(() => {
    const list = (data?.region || []).map((r) => ({
      id: r.id,
      name: r.name ?? '',
    }));
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  useEffect(() => {
    if (open) setStaged(value || []);
  }, [open, value]);

  const toggleItem = (id: string) => {
    setStaged((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
  };

  const apply = () => {
    onApply(staged.length > 0 ? staged : undefined);
    setOpen(false);
  };

  const selectedNames = (value || [])
    .map((id) => regions.find((r) => r.id === id)?.name)
    .filter((n): n is string => Boolean(n));
  const isActive = selectedNames.length > 0;
  const { label, more } = buildMultiChipLabel(selectedNames);

  return (
    <div className="pill-wrap" ref={wrapRef}>
      <FilterPill
        placeholder="Region"
        chipLabel={isActive ? label : undefined}
        chipMore={isActive ? more : undefined}
        open={open}
        onToggle={() => setOpen((v) => !v)}
        onClear={isActive ? onClear : undefined}
        a11yLabel="Filter by region"
      />
      <FilterDropdown
        open={open}
        onClose={() => setOpen(false)}
        onApply={apply}
        anchorRef={wrapRef}
        panelStyle={{ minWidth: 220 }}
      >
        <div className="dropdown-section-label">Region</div>
        {regions.map((r) => {
          const checked = staged.includes(r.id);
          return (
            <div
              key={r.id}
              className="dropdown-option"
              role="checkbox"
              aria-checked={checked}
              onClick={() => toggleItem(r.id)}
            >
              <span className={classNames('dropdown-check', { checked })}>
                {checked ? <FontAwesomeIcon icon={faCheck} fixedWidth /> : null}
              </span>
              {r.name}
            </div>
          );
        })}
      </FilterDropdown>
    </div>
  );
};

export default RegionPill;
